import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteRecordRaw,
  createWebHashHistory,
} from "vue-router";
import GetStartedView from "../views/GetStartedView.vue";
import OtpValidationView from "../views/OtpValidationView.vue";
import EditEmailView from "../views/EditEmailView.vue";
import ProfileInfoView from "../views/ProfileInfoView.vue";
import DocumentView from "../views/DocumentView.vue";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import DashboardLayout from "@/components/layouts/DashboardLayout.vue";
import TokenService from "@/services/tokenService";
import DependentsView from "../views/DependentsView.vue";
import PaymentsView from "../views/PaymentsView.vue";
import BenefitsView from "../views/BenefitsView.vue";
import StripeCardView from "../views/StripeCardView.vue";
import ApplicationSuccessView from "../views/ApplicationSuccessView.vue";
import routesPath from "@/config/routesPath";
import ConfirmationView from "../views/ConfirmationView.vue";
import ChallengeView from "../views/ChallengeView.vue";
import ForgotUsernameView from "../views/ForgotUsernameView.vue";
import ForgotPasswordView from "../views/ForgotPasswordView.vue";
import MFAView from "../views/MFAView.vue";
import common from "@/config/common";
import BlankView from "@/views/BlankView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import ParticipantEmailView from "../views/ParticipantEmailView.vue";
import ParticipantOtpValidationView from "../views/ParticipantOtpValidationView.vue";
import EnrollmentsView from "../views/EnrollmentsView.vue";
import store from "@/store";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: DefaultLayout,
    children: [
      {
        path: "",
        name: routesPath.getStarted,
        component: GetStartedView,
      },
      {
        path: routesPath.otpVerification,
        name: routesPath.otpVerification,
        component: OtpValidationView,
      },
      {
        path: routesPath.participantOTP,
        name: routesPath.participantOTP,
        component: ParticipantOtpValidationView,
      },
      {
        path: routesPath.participantEmail,
        name: routesPath.participantEmail,
        component: ParticipantEmailView,
      },
      {
        path: routesPath.editEmail,
        name: routesPath.editEmail,
        component: EditEmailView,
      },
      {
        path: routesPath.challenge,
        name: routesPath.challenge,
        component: ChallengeView,
      },
      {
        path: routesPath.forgotPassword,
        name: routesPath.forgotPassword,
        component: ForgotPasswordView,
      },
      {
        path: routesPath.forgotUsername,
        name: routesPath.forgotUsername,
        component: ForgotUsernameView,
      },
      {
        path: routesPath.mfa,
        name: routesPath.mfa,
        component: MFAView,
      },
      {
        path: routesPath.resetPassword,
        name: routesPath.resetPassword,
        component: ResetPasswordView,
      },
      {
        path: routesPath.pAndALoginSuccess,
        name: routesPath.pAndALoginSuccess,
        component: BlankView,
      },
    ],
  },
  {
    path: "/dashboard",
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: routesPath.enrollments,
        name: routesPath.enrollments,
        component: EnrollmentsView,
      },
      {
        path: routesPath.profileInformation,
        name: routesPath.profileInformation,
        component: ProfileInfoView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.dependents,
        name: routesPath.dependents,
        component: DependentsView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.benefits,
        name: routesPath.benefits,
        component: BenefitsView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.documents,
        name: routesPath.documents,
        component: DocumentView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.payments,
        name: routesPath.payments,
        component: PaymentsView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.confirmation,
        name: routesPath.confirmation,
        component: ConfirmationView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.cardDetails,
        name: routesPath.cardDetails,
        component: StripeCardView,
        meta: { requiresActiveEnrollment: true },
      },
      {
        path: routesPath.applicationSuccessful,
        name: routesPath.applicationSuccessful,
        component: ApplicationSuccessView,
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: DefaultLayout,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  //history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const hasAccesstoken = TokenService.getAccessToken();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  if (requiresAuth && !hasAccesstoken) {
    sessionStorage.clear();
    next("/");
  } else if (requiresAuth && hasAccesstoken) {
    const requiresActiveEnrollment = to.matched.some(
      (record) => record.meta.requiresActiveEnrollment
    );

    if (requiresActiveEnrollment) {
      const enrollment = getEnrollmentFromStorage();

      if (isEnrollmentSubmitted(enrollment)) {
        next({ name: routesPath.applicationSuccessful });
        return;
      }
    }

    customNext(to.name, from.name, next);
  } else if (
    to.name === routesPath.otpVerification ||
    to.name === routesPath.editEmail
  ) {
    const storedData = sessionStorage.getItem(
      common.storageVariables.loginData
    );
    const loginData = storedData ? JSON.parse(storedData) : null;
    if (!loginData || (!loginData.ssn && !loginData.registrationCode)) {
      next("/");
    } else {
      next();
    }
  } else {
    next(); // Non-protected route, proceed with navigation
  }
});

function isEnrollmentSubmitted(enrollment: any): boolean {
  if (!enrollment) return false;

  return (
    (enrollment.serviceType === common.serviceType.Cobra &&
      enrollment.cobra?.status === 10) 
  );
}

function getEnrollmentFromStorage() {
  const enrollment = store.getters.getEnrollment;

  if (enrollment) return enrollment;

  const stored = sessionStorage.getItem(common.storageVariables.enrollment);
  return stored ? JSON.parse(stored) : null;
}

function customNext(currentName: any, fromName: any, next: any) {
  const allRoutes = common.allRoutes;
  if (
    fromName === undefined &&
    allRoutes.some((item) => item.link === currentName)
  ) {
    const lastRouteName = localStorage.getItem(
      common.storageVariables.currentRoute
    );
    const lastRoute = allRoutes.find((route) => route.link === lastRouteName);
    const currentRoute = allRoutes.find((route) => route.link === currentName);
    if (lastRoute && currentRoute) {
      if (currentRoute.priority <= lastRoute.priority) {
        next();
      } else {
        next({ name: lastRouteName });
      }
    } else if (currentName !== routesPath.profileInformation) {
      next({ name: routesPath.profileInformation });
    } else {
      next();
    }
  } else {
    next();
  }
}

export default router;
